import { Component, EventEmitter, inject, Input, Output, Type, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { Menu } from 'primeng/menu';
import { MenuItem, SelectItem } from 'primeng/api';
import { TableColResizeEvent, TableColumnReorderEvent, TableLazyLoadEvent } from 'primeng/table';
import { Popover } from 'primeng/popover';

// Components
import { BaseComponent, CONVERSATION_QUERY_PARAM, SELECTED_QUERY_PARAM } from './base.component';
import { MassDeleteRefusalsComponent } from '../mass-delete-refusals/mass-delete-refusals.component';

// Services
import { Logger } from 'src/app/@core/services/logger/logger.service';
import { MessageServiceBroker } from 'src/app/@core/services/message.service';
import { ServiceBase } from 'src/app/@core/services/service-base';
import { TableState } from 'src/app/@core/services/table-state.service';

// Modèles
import { ModelBase } from '../../models/abstract/model-base.model';

// Utils
import { Filter } from '../../utilities/models/filter';
import { MassDeleteLabels } from '../mass-delete-refusals/mass-delete-refusals.model';
import { MassDeleteResponse, MASS_DELETE_SUMMARY_CODE, massDeleteSummaryMessage } from '../../utilities/odata/mass-delete';
import { QueryOptions } from '../../utilities/odata/query-params';
import { tableLazyLoadEventToUrl, tableStateToUrl } from '../../utilities/odata/table-lazy-load-event-to-url';

const LOGGER = new Logger('TableBaseComponent');

export interface TableColumnDef {
    /** Identifiant unique de la colonne */
    field: string;
    /** Libellé affiché dans l'entête */
    header: string;
    /** Colonne affichée ou non */
    visible: boolean;
    /** Champ de tri OData (si la colonne est triable) */
    sortField?: string;
    /** Classe CSS appliquée au <th> */
    headerClass?: string;
    /** Classe CSS appliquée au <td> */
    cellClass?: string;
    /** Libellé dans le sélecteur de colonnes (si différent de header) */
    label?: string;
    /** Largeur en pixels (définie dans le code ou par l'utilisateur via resize) */
    columnWidth?: number;
}

@Component({
    selector: 'biifor-table-base',
    template: `
        NO UI TO BE FOUND HERE!
    `,
    standalone: false
})
export class TableBaseComponent extends BaseComponent {

    protected messageService = inject(MessageServiceBroker);

    @Input()
    public exportable: boolean = false;
    @Input()
    public flat: boolean = false;
    @Input()
    public hasActiveFilters: boolean = false;
    @Input()
    public lazy: boolean = false;
    @Input()
    public lazyLoadOnInit: boolean = true;
    @Input()
    public paginator: boolean = true;
    @Input()
    public showTitle: boolean = true;
    @Input()
    public showFilters: boolean = false;
    /**
     * Affiche le bouton de partage du lien de la vue (#961).
     *
     * Opt-in, comme `showFilters` : c'est la **page** qui le pose, jamais le tableau. Une
     * même table sert de liste sur son écran et d'extrait sur une fiche — les factures d'un
     * devis, les transactions d'une règle —, et un lien n'a de sens que dans le premier cas.
     */
    @Input()
    public shareable: boolean = false;
    public filtersCount: number = 0;
    @Input()
    public override totalRecords: number = 0;

    // Gestion de l'état du tableau
    @Input()
    public stateId: string | null = null;
    private _currentState: TableState | null = null;
    private _previousSortField: string | string[] | null | undefined = null;
    private _previousSortOrder: number | number[] | null | undefined = null;

    // S barre de filtres
    public selectedFilteringOptions: string[] = [];

    // Éléments de la vue
    @ViewChild('dt', { static: true }) dt!: any;
    @ViewChild('itemActionsMenu', { static: true }) itemActionsMenu!: Menu;

    // Évènements
    @Output() lazyLoad = new EventEmitter();
    @Output() refresh = new EventEmitter();
    @Output() rowsDeleted = new EventEmitter();
    @Output() rowsEdited = new EventEmitter();
    @Output() tableReady = new EventEmitter();

    public dateMatchModes: SelectItem[] = Filter.DateComparators;
    public numberMatchModes: SelectItem[] = Filter.NumericComparators;
    public textMatchModes: SelectItem[] = Filter.TextComparators;

    /** Nombre maximum de documents acceptés par la route de téléchargement groupé */
    public readonly massDownloadMaxItems: number = 100;

    /**
     * Ids que la dernière suppression groupée n'a pas eu le temps d'examiner.
     *
     * Ni supprimés, ni refusés : le serveur a rendu la main avant la coupure du routeur, et
     * c'est au front de reproposer la suite. Ils survivent au rechargement de la liste, le
     * temps que `takePendingDeletionRows()` les retrouve et les recoche.
     */
    private _pendingDeletionIds: number[] = [];

    // Menu
    public itemActionsMenuItems: MenuItem[] = [];

    /*----------------------------------------------------------------------------------------
     * Colonnes
     ----------------------------------------------------------------------------------------*/

    public columns: TableColumnDef[] = [];

    public get visibleColumns(): TableColumnDef[] {
        return this.columns.filter(col => col.visible);
    }

    // Sélecteur de colonnes
    @ViewChild('columnPickerPopover') columnPickerPopover!: Popover;

    /*----------------------------------------------------------------------------------------
     * Détail d'une ligne dans l'adresse (#995)
     *
     * L'adresse d'une liste dont le détail s'affiche dans une fenêtre peut désigner une ligne
     * précise — `?selected=<id>`. Ouvrir cette adresse ouvre la fenêtre ; la fermer rend
     * l'adresse de la liste seule.
     *
     * Le fil de discussion à afficher par-dessus voyage dans la même adresse
     * (`&conversation=<id>`), mais **ne transite pas par ici** : `ConversationIndicatorComponent`
     * le lit directement, comme il lit déjà l'adresse pour trouver son objet porteur. Rien à
     * relayer, donc, et les écrans qui ont une page à eux en bénéficient de la même façon.
     *
     * La ligne est lue **à l'unité** sur le serveur, et non cherchée parmi les lignes
     * affichées : c'est ce qui la fait s'ouvrir même quand elle est en page 3, ou écartée par
     * les filtres mémorisés du destinataire. Le lien prime sur les préférences d'affichage.
     *
     * Une liste s'équipe en redéfinissant `loadSelectedRow$()` et `openRowDialog()`, et en
     * appelant `selectRow()` depuis son gestionnaire de clic. Tant que `loadSelectedRow$()`
     * n'est pas redéfinie, l'écran ignore le paramètre.
     *
     * ⚠️ La fenêtre de détail doit étendre `DialogBaseComponent`, et `openRowDialog()` doit
     * l'ouvrir par `openRowDetailDialog()` — sans quoi l'adresse continuerait de désigner une
     * ligne dont la fenêtre est fermée, et un retour sur cette même ligne ne la rouvrirait
     * pas. C'est aussi `DialogBaseComponent` qui décide de fermer la fenêtre quand l'adresse
     * change de ligne ou d'écran. Cf. `_openSelectedRow()` sur le cycle ouverture / fermeture.
     ----------------------------------------------------------------------------------------*/

    /**
     * Ligne dont la fenêtre est ouverte, pour ne pas la rouvrir par-dessus elle-même.
     *
     * Va de pair avec `DialogBaseComponent._shouldCloseOnNavigation()` : la fenêtre ne se
     * ferme plus sur une navigation qui ne fait que réémettre le tri, la pagination ou les
     * filtres de la liste, donc le socle ne doit pas non plus la rouvrir. Retirer l'un des
     * deux sans l'autre donne soit une fenêtre qui clignote, soit deux fenêtres empilées.
     */
    private _openedRowId: number | null = null;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    override ngOnInit() {
        // Call parent method
        super.ngOnInit();
        // Charger l'état du tableau si stateId est défini
        this._loadTableState();
        // Classes de la table
        this.applyTableClasses();
    }

    public onChangeColumnsConfig(columns: TableColumnDef[]): void {
        this.columns = columns;
    }

    public onClickColumnPicker(event: MouseEvent): void {
        this.columnPickerPopover.toggle(event);
    }

    public onClickItemActions(event: MouseEvent, item: any): void {
        this.fillItemMenuItems(item);
        this.itemActionsMenu.toggle(event);
    }

    public onClickRefresh(): void {
        this.refresh.emit();
    }

    /**
     * Gère le réordonnancement des colonnes via drag & drop.
     * Met à jour l'ordre du tableau columns et sauvegarde le nouvel état si stateId est défini.
     * À utiliser avec l'événement (onColReorder) de PrimeNG Table.
     * @param event TableColumnReorderEvent (contient dragIndex et dropIndex)
     */
    public onColReorder(event: TableColumnReorderEvent): void {
        // event.dragIndex: index de la colonne déplacée
        // event.dropIndex: nouvel index de la colonne
        if (!event || event.dragIndex === undefined || event.dropIndex === undefined) return;
        // Réordonner columns
        const movedCol = this.columns.splice(event.dragIndex, 1)[0];
        this.columns.splice(event.dropIndex, 0, movedCol);

        // Sauvegarder le nouvel ordre si stateId défini
        if (this.stateId) {
            const state: TableState = this._currentState ? { ...this._currentState } : {};
            state.columnConfig = this.columns.map(col => ({
                field: col.field,
                visible: col.visible,
                ...(col.columnWidth !== undefined ? { width: col.columnWidth } : {}),
            }));
            this._saveTableState(state);
        }
    }

    public onColResize(event: TableColResizeEvent): void {
        const field = (event.element as HTMLElement).dataset['field'];
        if (!field) return;

        const col = this.columns.find(c => c.field === field);
        if (!col) return;

        col.columnWidth = event.element.offsetWidth;

        if (!this.stateId) return;
        const state: TableState = this._currentState ? { ...this._currentState } : {};
        state.columnConfig = this.columns.map(c => ({
            field: c.field,
            visible: c.visible,
            ...(c.columnWidth !== undefined ? { width: c.columnWidth } : {}),
        }));
        this._saveTableState(state);
    }

    public onColumnPickerHide(): void {
        if (!this.stateId) return;

        const state: TableState = this._currentState ? { ...this._currentState } : {};
        state.columnConfig = this.columns.map(col => ({
            field: col.field,
            visible: col.visible,
            ...(col.columnWidth !== undefined ? { width: col.columnWidth } : {}),
        }));
        this._saveTableState(state);
    }

    public onFilterGlobal($event: any, stringVal: any) {
        this.dt.filterGlobal(($event.target as HTMLInputElement).value, stringVal);
    }

    public onLazyLoad(event: TableLazyLoadEvent) {
        this.handleLazyLoading(event);
    }

    public onSelectedFilteringOptionsChange(selectedFilteringOptions: string[]) {
        if (!!this.stateId && !!this._currentState) {
            this.selectedFilteringOptions = selectedFilteringOptions;
            this._currentState.selectedFilteringOptions = selectedFilteringOptions;
            this._saveTableState(this._currentState);
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override afterNavigation(initial: boolean): void {
        super.afterNavigation(initial);
        this._openSelectedRow();
    }

    /**
     * Applique les classes CSS au tableau en fonction des options
     */
    protected applyTableClasses(): void {
        if (this.dt?.el?.nativeElement) {
            // Ajout de la classe flat si nécessaire
            if (this.flat) {
                this.dt.el.nativeElement.classList.add('flat');
            }
            // Ajout de la classe no-title si nécessaire
            if (!this.showTitle) {
                this.dt.el.nativeElement.classList.add('no-title');
            }
        }
    }

    /**
     * Déclenche un export CSV via le service fourni.
     * Construit les options de requête à partir de l'URL courante (filtres + tri),
     * supprime la pagination et déclenche le téléchargement du Blob renvoyé par le serveur.
     * Le nom de fichier est extrait de l'en-tête Content-Disposition de la réponse.
     * @param service Le service exposant la route `export`
     */
    protected exportCsv(service: ServiceBase): void {
        const queryOptions = QueryOptions.fromUrlString(this.router.url);
        queryOptions.skip = 0;
        queryOptions.top = 1000000;

        service.export$(queryOptions).subscribe((response: HttpResponse<Blob>) => {
            this._downloadBlobResponse(response);
        });
    }

    protected fillItemMenuItems(_: any): void {
        if (!this.itemActionsMenuItems.length) {
            this.itemActionsMenuItems.push({
                label: 'Aucune action disponible',
                icon: 'pi pi-ban',
                disabled: true,
            });
        }
    }

    protected handleLazyLoading(event: TableLazyLoadEvent): void {
        // Détecter si le tri a changé
        const sortChanged = this._hasSortChanged(event.sortField, event.sortOrder);

        // Si le tri a changé, réinitialiser la pagination à la première page
        if (sortChanged && event.first !== 0) {
            event.first = 0;
            if (this.dt) {
                this.dt.first = 0;
            }
        }

        // Mémoriser le tri actuel pour la prochaine comparaison
        this._previousSortField = event.sortField;
        this._previousSortOrder = event.sortOrder;

        // Sauvegarder l'état du tableau si la fonctionnalité est activée
        this._updateTableStateFromLazyLoading(event);
        // Mise à jour de l'URL pour prendre en compte les instructions de la table
        this._updateUrl(event);
    }

    /**
     * Lecture unitaire de la ligne désignée par l'adresse.
     *
     * À redéfinir par une liste dont le détail s'affiche dans une fenêtre — c'est ce qui
     * l'équipe. Renvoyer `null` (défaut) laisse l'écran ignorer le paramètre `selected`.
     *
     * Redéfinir avec les mêmes options de lecture que l'ouverture par clic : une fenêtre
     * ouverte depuis un lien doit afficher exactement ce que la même fenêtre affiche depuis
     * la liste, expands compris.
     *
     * @param id L'identifiant technique de la ligne
     */
    protected loadSelectedRow$(id: number): Observable<any> | null {
        return null;
    }

    /**
     * Supprime une sélection en **une seule demande**, et rend compte de ce qui s'est passé.
     *
     * Remplace la boucle « un `DELETE` par ligne », qui atteignait le plafond de débit de
     * l'API et faisait rejeter la majorité d'une grosse sélection sans le moindre message
     * (#1075) — et qui, sur un refus métier, interrompait la série sans recharger la
     * liste (#1036).
     *
     * Trois choses en découlent, et elles sont toutes les trois nécessaires :
     *
     * - la **synthèse est composée ici**, pas relayée : le serveur classe sa synthèse en
     *   succès même quand rien n'est parti, ce qui donnerait un bandeau vert sur un échec
     *   total. Les autres messages de la réponse, eux, sont relayés tels quels ;
     * - les **refus sont détaillés dans une fenêtre**, chacun nommé avec son motif : un lot
     *   peut en aligner des centaines, ce qu'aucune notification ne sait porter ;
     * - les ids **non examinés** sont mémorisés pour être recochés au rechargement de la
     *   liste — cf. {@link takePendingDeletionRows}. C'est le seul survivant de la
     *   sélection, qui est perdue au rechargement.
     *
     * `rowsDeleted` est émis dans tous les cas où le serveur a répondu, y compris quand
     * tout a été refusé : la liste doit se recharger pour montrer son état réel.
     *
     * La liste est **bloquée** le temps de la demande (`loading`, le voile du `p-table`) :
     * un lot peut prendre une vingtaine de secondes, et un second clic pendant ce temps
     * relancerait la même sélection. Le voile tombe à la réponse, y compris quand la
     * demande est refusée en bloc (4xx / 5xx) — c'est le `finalize`, pas le `next`.
     *
     * @param service Le service métier exposant la route `massDelete`
     * @param ids Les ids des objets à supprimer, dans l'ordre de la sélection
     * @param labels Comment nommer ces objets dans les messages
     */
    protected massDelete(service: ServiceBase, ids: number[], labels: MassDeleteLabels): void {
        this._pendingDeletionIds = [];
        this.loading = true;

        service.massDeleteSelection$(ids).pipe(
            finalize(() => this.loading = false)
        ).subscribe((response: MassDeleteResponse | null) => {
            // Demande refusée en bloc (charge malformée, dossier absent) : `handleError` a
            // déjà notifié le motif, et rien n'a bougé — ni la liste, ni la sélection.
            if (!response) return;

            // La synthèse du serveur est remplacée par la nôtre ; le reste — dont
            // l'avertissement de lot interrompu — est affiché tel quel.
            this.messageService.sendODataMessages(
                response.messages.filter(message => message.code !== MASS_DELETE_SUMMARY_CODE)
            );

            this._notifyMassDeleteSummary(response, labels);

            if (response.refused.length) {
                this.openDialog(MassDeleteRefusalsComponent, 'MASS_DELETE_REFUSALS_DIALOG', {
                    refusals: response.refused,
                    labels: labels
                });
            }

            this._pendingDeletionIds = response.pending;
            this.rowsDeleted.emit();
        });
    }

    /**
     * Déclenche le téléchargement groupé des PDF des documents passés en paramètre.
     * L'archive est streamée par le serveur, aucun écran d'attente n'est nécessaire.
     * Le nom de fichier retenu est celui de l'en-tête Content-Disposition de la réponse.
     * @param service Le service métier exposant la route `massDownload`
     * @param ids Les ids des documents dont le PDF est demandé
     * @param fileName Le nom de l'archive
     */
    protected massDownload(service: ServiceBase, ids: number[], fileName: string): void {
        service.massDownload$(ids, fileName).subscribe((response: HttpResponse<Blob>) => {
            this._downloadBlobResponse(response);
        });
    }

    /**
     * Prépare le téléchargement groupé des pièces jointes d'un objet porteur.
     * L'archive est streamée par le serveur, qui la nomme : le nom retenu est celui de
     * l'en-tête Content-Disposition de la réponse.
     * L'observable est **rendu et non souscrit**, pour que l'appelant y branche son propre
     * indicateur d'activité — la préparation d'une archive volumineuse n'est pas instantanée.
     * @param service Le service métier de l'objet porteur
     * @param parent L'objet qui porte les pièces jointes
     * @param ids Les ids des pièces jointes demandées
     * @param navigation La collection de pièces jointes de l'objet porteur
     */
    protected massDownloadAttachments$(service: ServiceBase, parent: any, ids: number[], navigation: string = 'attachments'): Observable<HttpResponse<Blob>> {
        return service.attachmentsMassDownload$(parent, ids, navigation).pipe(
            tap((response: HttpResponse<Blob>) => this._downloadBlobResponse(response))
        );
    }

    protected modifyTableState(state: TableState): void {
        // Méthode à override dans les classes filles si besoin
    }

    /**
     * Ouvre la fenêtre de détail de la ligne lue par `loadSelectedRow$()`.
     *
     * À redéfinir en même temps qu'elle : la clé de configuration de la fenêtre et le nom de
     * la donnée attendue diffèrent d'un écran à l'autre.
     *
     * ⚠️ Passer par `openRowDetailDialog()` et non par `openDialog()`, sans quoi la fenêtre ne
     * rendra pas l'adresse à la liste en se fermant.
     *
     * @param instance La ligne lue sur le serveur
     */
    protected openRowDialog(instance: any): void {
        // Méthode à override par les listes équipées
    }

    /**
     * Ouvre une fenêtre **qui affiche la ligne désignée par l'adresse**, et le lui dit.
     *
     * C'est ici, et seulement ici, que naît le nettoyage de l'adresse à la fermeture : la
     * fenêtre elle-même ne peut pas savoir si elle a été ouverte par un lien ou par un select
     * à création rapide, un menu, une carte. Un appelant qui n'est pas ce socle n'a donc rien
     * à déclarer — et surtout rien à nettoyer.
     */
    protected openRowDetailDialog(component: Type<any>, config: string, data: any = {}): string {
        return this.openDialog(component, config, { ...data, clearsSelectedOnClose: true });
    }

    /**
     * Désigne une ligne dans l'adresse, ce qui déclenche l'ouverture de sa fenêtre de détail.
     *
     * À appeler depuis le gestionnaire de clic de la liste, à la place d'un `openDialog()`
     * direct : c'est le passage par l'adresse qui rend le lien copiable et partageable.
     *
     * L'entrée est empilée dans l'historique — et non remplacée — pour que le retour arrière
     * ramène à la liste.
     *
     * @param item La ligne cliquée
     */
    protected selectRow(item: any): void {
        if (!item?.id) return;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [SELECTED_QUERY_PARAM]: item.id },
            queryParamsHandling: 'merge'
        });
    }

    /**
     * Rend les lignes qu'une suppression groupée interrompue n'a pas examinées, pour les
     * recocher — et oublie ce lot au passage, qui ne vaut que pour un rechargement.
     *
     * À appeler depuis le setter des données de la liste : la sélection ne survit pas au
     * rechargement, et les ids « non examinés » en sont le seul survivant. Sans ce
     * rétablissement, l'utilisateur devrait retrouver ses lignes une par une pour relancer
     * la suppression sur ce qui reste.
     *
     * Rend `null` — et non un tableau vide — quand aucun lot n'attend : la liste se
     * recharge pour bien d'autres raisons qu'une suppression, et ces rechargements-là ne
     * doivent pas toucher à la sélection en cours.
     *
     * @param rows Les lignes qui viennent d'être chargées
     * @return Les lignes à cocher, ou `null` s'il n'y a pas de lot en attente
     */
    protected takePendingDeletionRows<T extends ModelBase>(rows: T[]): T[] | null {
        if (!this._pendingDeletionIds.length) return null;

        const pendingIds = this._pendingDeletionIds;
        this._pendingDeletionIds = [];

        return rows.filter(row => pendingIds.includes(row.id));
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Applique la configuration de colonnes sauvegardée.
     * Les colonnes présentes dans le state reprennent l'ordre et la visibilité sauvegardés.
     * Les colonnes absentes du state (nouvelles colonnes ajoutées au code) sont ajoutées à la fin
     * avec leur valeur par défaut.
     */
    private _applyColumnConfig(columnConfig: { field: string; visible: boolean; width?: number }[]): void {
        const savedConfigMap = new Map(columnConfig.map(c => [c.field, c]));
        const orderedColumns: TableColumnDef[] = [];

        // Colonnes dans l'ordre sauvegardé
        for (const savedCol of columnConfig) {
            const col = this.columns.find(c => c.field === savedCol.field);
            if (col) {
                orderedColumns.push({
                    ...col,
                    visible: savedCol.visible,
                    ...(savedCol.width !== undefined ? { columnWidth: savedCol.width } : {}),
                });
            }
        }

        // Colonnes non présentes dans l'état sauvegardé (ajoutées au code après la sauvegarde)
        for (const col of this.columns) {
            if (!savedConfigMap.has(col.field)) {
                orderedColumns.push(col);
            }
        }

        this.columns = orderedColumns;
    }

    /**
     * Applique l'état chargé au tableau
     *
     * Sans effet une fois le composant détruit (#1025) : la navigation qui suit est relative
     * à la route de cette liste, mais fusionne les paramètres de l'adresse courante — celle de
     * la liste suivante. Elle ramènerait l'utilisateur ici avec le tri d'une autre liste.
     */
    private _applyTableStateToTable(state: TableState): void {
        if (this.subscriptions.closed) return;

        if (!this.dt) {
            // Si le tableau n'est pas encore initialisé, réessayer dans un court délai
            setTimeout(() => this._applyTableStateToTable(state), 100);
            return;
        }

        // Appliquer le table state au tableau PrimeNG
        if (state.first !== undefined) {
            this.dt.first = state.first;
        }
        if (state.rows !== undefined) {
            this.dt.rows = state.rows;
        }
        if (state.sortField) {
            this.dt.sortField = state.sortField;
        }
        if (state.sortOrder) {
            this.dt.sortOrder = state.sortOrder;
        }

        // Forcer la mise à jour visuelle du tableau
        if (this.dt.sortField || this.dt.sortOrder) {
            const lazy = this.dt.lazy;
            this.dt.lazy = false;
            this.dt.sortSingle();
            this.dt.lazy = lazy;
        }

        // Sélection des options de filtrage
        if (state.selectedFilteringOptions) {
            this.selectedFilteringOptions = state.selectedFilteringOptions;
        }

        // Configuration des colonnes (ordre et visibilité)
        if (state.columnConfig) {
            this._applyColumnConfig(state.columnConfig);
        }

        // Conversion de l'état du tableau en URL OData
        const odataQueryParams = tableStateToUrl(state);
        LOGGER.debug('Paramètres OData générés:', odataQueryParams);

        // Navigation vers l'URL avec les paramètres OData
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: this.parseQueryString(odataQueryParams),
            queryParamsHandling: 'merge'
        }).then(() => {
            // Émettre l'événement après la navigation
            this.tableReady.emit();
        });
    }

    /**
     * Rend à l'adresse la liste seule, sans empiler d'entrée d'historique.
     *
     * Appelé quand la ligne visée est introuvable : sans ce nettoyage, l'adresse porterait
     * une ligne qui n'existe pas, et chaque rafraîchissement rejouerait l'erreur.
     */
    private _clearSelectedFromUrl(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [SELECTED_QUERY_PARAM]: null, [CONVERSATION_QUERY_PARAM]: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    /**
     * Compare deux objets de manière profonde, indépendamment de l'ordre des clés
     */
    private _deepEqual(obj1: any, obj2: any): boolean {
        if (obj1 == null || obj2 == null) return false;

        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        for (const key of keys1) {
            if (!keys2.includes(key)) return false;
            if (!this._deepEqual(obj1[key], obj2[key])) return false;
        }

        return true;
    }

    /**
     * Déclenche le téléchargement d'un Blob renvoyé par l'API.
     * Le nom de fichier est extrait de l'en-tête Content-Disposition (RFC 5987 ou format simple).
     */
    private _downloadBlobResponse(response: HttpResponse<Blob>): void {
        const blob = response.body;
        if (!blob) return;

        const filename = this._extractFilename(response.headers.get('Content-Disposition')) ?? 'export.csv';
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Extrait le nom de fichier depuis un en-tête Content-Disposition.
     * Gère le format RFC 5987 (`filename*=UTF-8''nom.csv`) puis fallback sur `filename="nom.csv"`.
     */
    private _extractFilename(header: string | null): string | null {
        if (!header) return null;

        const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
        if (utf8Match) {
            return decodeURIComponent(utf8Match[1].trim());
        }

        const simpleMatch = /filename="?([^";]+)"?/i.exec(header);
        if (simpleMatch) {
            return simpleMatch[1].trim();
        }

        return null;
    }

    /**
     * Récupère l'état du tableau à partir des paramètres de l'URL
     * @param existingState TableState optionnel pour fusionner avec l'état existant
     * @returns TableState
     */
    private _getStateFromUrl(existingState?: TableState): TableState {

        let state: TableState = existingState ? { ...existingState } : {};

        state.first = this.queryParams.get('$skip') ? parseInt(this.queryParams.get('$skip')!, 10) : state.first ? state.first : 0;
        state.rows = this.queryParams.get('$top') ? parseInt(this.queryParams.get('$top')!, 10) : state.rows ? state.rows : 25;
        const sortField = this.queryParams.get('$orderby') || null;
        if (sortField && sortField.includes(' desc')) {
            state.sortOrder = -1;
            state.sortField = sortField.replace(' desc', '');
        } else if (sortField) {
            state.sortField = sortField;
            state.sortOrder = 1;
        } else if (!existingState) {
            state.sortField = null;
            state.sortOrder = null;
        }
        return state;
    }

    private _handleTableState(state: TableState): void {

        this.modifyTableState(state);

        if (state && Object.keys(state).length > 0) {
            this._currentState = { ...state };
            // Metre à jour le table state à partir des paramètres de l'URL, qui sont prioritaires
            this._updateTableStateFromUrl(state);
            // Appliquer l'état au tableau
            this._applyTableStateToTable(state);
        } else {
            // Émettre l'événement tableReady même si aucun état n'est défini
            this.tableReady.emit();
        }
    }

    /**
     * Vérifie si le tri a changé par rapport à l'état précédent
     */
    private _hasSortChanged(
        newSortField: string | string[] | null | undefined,
        newSortOrder: number | number[] | null | undefined
    ): boolean {
        // Comparaison du champ de tri
        const sortFieldChanged = JSON.stringify(this._previousSortField) !== JSON.stringify(newSortField);
        // Comparaison de l'ordre de tri
        const sortOrderChanged = JSON.stringify(this._previousSortOrder) !== JSON.stringify(newSortOrder);

        return sortFieldChanged || sortOrderChanged;
    }

    /**
     * Charge l'état du tableau depuis le backend
     */
    private _loadTableState(): void {
        // Si aucun stateId n'est défini, on émet directement l'évènement tableReady
        if (!this.stateId) {
            this.tableReady.emit({ navigationTriggered: false });
            return;
        }

        // Charger l'état du tableau depuis le service — rattaché au cycle de vie du composant :
        // une réponse arrivée après le départ de la liste ne doit pas être appliquée (#1025)
        this.subscriptions.add(
            this.tableStateService.getTableState$(this.stateId).pipe(
                map((state: TableState) => {
                    this._handleTableState(state);
                })
            ).subscribe({
                error: (error) => {
                    LOGGER.error('Erreur lors du chargement de l\'état du tableau:', error);
                    this.tableReady.emit({ navigationTriggered: false });
                }
            })
        );
    }

    /**
     * Notifie le résultat d'ensemble d'une suppression groupée — la phrase est composée par
     * {@link massDeleteSummaryMessage}, partagée avec les écrans hors table de base.
     *
     * Le détail des refus, lui, vit dans la fenêtre ouverte par {@link massDelete} : une
     * notification ne sait pas porter des centaines de lignes.
     *
     * @param response Le rapport du serveur
     * @param labels Comment nommer les objets supprimés
     */
    private _notifyMassDeleteSummary(response: MassDeleteResponse, labels: MassDeleteLabels): void {
        this.messageService.sendMessage(massDeleteSummaryMessage(response, labels));
    }

    /**
     * Ouvre la fenêtre de détail de la ligne désignée par l'adresse, s'il y en a une.
     *
     * L'arrivée sur une adresse partagée produit plusieurs navigations : l'état de table
     * (`_applyTableStateToTable`) puis le premier chargement paresseux (`_updateUrl`)
     * réémettent chacun tri et pagination en `merge`, en conservant `selected`. La ligne déjà
     * ouverte est donc mémorisée, sans quoi chacune de ces navigations relancerait la lecture
     * et empilerait une fenêtre.
     *
     * Une ligne introuvable — supprimée depuis l'envoi du lien, ou hors des droits du
     * destinataire — n'est pas distinguable de l'autre : le serveur renvoie un 404 dans les
     * deux cas, et le message générique du socle HTTP l'annonce sans rien révéler. L'adresse
     * ne peut donc pas servir à tester l'existence d'une ligne.
     */
    private _openSelectedRow(): void {
        const raw = this.queryParams?.get(SELECTED_QUERY_PARAM);
        const selectedId = raw ? parseInt(raw, 10) : NaN;

        // Adresse sans ligne : on oublie la ligne ouverte, pour qu'un retour sur la même
        // ligne la rouvre.
        if (!Number.isInteger(selectedId) || selectedId <= 0) {
            this._openedRowId = null;
            return;
        }

        // Fenêtre déjà ouverte sur cette ligne.
        if (this._openedRowId === selectedId) return;

        const request$ = this.loadSelectedRow$(selectedId);
        // Liste non équipée : le paramètre ne la concerne pas.
        if (!request$) return;

        this._openedRowId = selectedId;
        this.appService.uiBlocked = true;

        request$.pipe(
            finalize(() => this.appService.uiBlocked = false),
            catchError(() => {
                this._openedRowId = null;
                this._clearSelectedFromUrl();
                return of(null);
            })
        ).subscribe((instance: any) => {
            if (!instance) return;
            this.openRowDialog(instance);
        });
    }

    /**
     * Sauvegarde l'état du tableau
     */
    private _saveTableState(state: TableState): void {
        // Si l'état a déjà été chargé, et qu'on souhaite sauvegardé un état identique, on ne sauvegarde pas
        if (this._currentState && this._deepEqual(this._currentState, state)) {
            LOGGER.debug('État du tableau identique, sauvegarde annulée:', state);
            return;
        }

        this.tableStateService.saveTableStateSilent(this.stateId!, state);
        LOGGER.debug('État du tableau sauvegardé (hors filtres):', state);
        this._currentState = JSON.parse(JSON.stringify(state));
    }

    /**
     * Mise à jour de l'état du tableau à partir de l'événement de lazy loading
     * @param event TableLazyLoadEvent
     * @returns void
     */
    private _updateTableStateFromLazyLoading(event: TableLazyLoadEvent) {
        if (!this.stateId) return;

        // Mettre à jour l'état chargé existant s'il existe
        const state: TableState = this._currentState ? { ...this._currentState } : {};
        state.sortField = event.sortField || null;
        state.sortOrder = event.sortOrder || null;
        state.first = event.first !== undefined ? event.first : state.first;
        state.rows = event.rows !== undefined ? event.rows : state.rows;
        state.multiSortMeta = event.multiSortMeta || [];

        this._saveTableState(state);
    }

    /**
     * Mise à jour de l'état du tableau à partir des paramètres de l'URL
     * @param state TableState
     * @returns void
     */
    private _updateTableStateFromUrl(state: TableState) {
        this._currentState = this._getStateFromUrl(state);

        this._saveTableState(state);
    }

    private _updateUrl(event: TableLazyLoadEvent) {
        // Composant détruit : cf. _applyTableStateToTable (#1025)
        if (this.subscriptions.closed) return;

        // Conversion de l'événement lazy load en URL OData
        const odataQueryParams = tableLazyLoadEventToUrl(event);
        LOGGER.debug('Paramètres OData générés:', odataQueryParams);

        // Navigation vers l'URL avec les paramètres OData
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: this.parseQueryString(odataQueryParams),
            queryParamsHandling: 'merge'
        });
    }
}
